import { CommonModule } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { executeNavigationFromConfig } from '../../shared-services/chart-wrapper.service';
import { formatDisplayText } from '../../utils/text-case.utils';
import type { TextCaseMode } from '../../utils/text-case.utils';

interface TextWidgetFontConfig {
  size?: string | number;
  weight?: string | number;
  style?: 'normal' | 'italic' | 'oblique';
  family?: string;
}

interface NavigationButtonStyleConfig {
  backgroundColor?: string;
  color?: string;
  borderColor?: string;
  borderRadius?: string | number;
  borderWidth?: string | number;
  boxShadow?: string;
  padding?: string;
  minHeight?: string | number;
  minWidth?: string | number;
  width?: string | number;
  justifyContent?: 'flex-start' | 'center' | 'flex-end' | 'space-between';
  alignItems?: 'flex-start' | 'center' | 'flex-end';
  font?: TextWidgetFontConfig;
}

interface TextWidgetNavigationOption {
  label?: string;
  text?: string;
  content?: string;
  moduleLabel?: string;
  subtitle?: string;
  topIcon?: string;
  bottomIcon?: string;
  route?: string;
  navigateTo?: string;
  path?: string;
  url?: string;
  navigateAction?: Record<string, any>;
  disabled?: boolean;
  buttonStyle?: NavigationButtonStyleConfig;
}

interface TextWidgetNavigationNConfig {
  text?: string;
  content?: string;
  label?: string;
  title?: string;
  moduleLabel?: string;
  subtitle?: string;
  topIcon?: string;
  bottomIcon?: string;
  navigateTo?: string;
  navigateAction?: Record<string, any>;
  textCase?: TextCaseMode;
  color?: string;
  align?: 'left' | 'center' | 'right' | 'justify';
  lineHeight?: string | number;
  letterSpacing?: string | number;
  wrap?: boolean;
  maxLines?: number;
  textOverflow?: 'clip' | 'ellipsis';
  font?: TextWidgetFontConfig;
  showIndicator?: boolean;
  openIndicator?: string;
  closeIndicator?: string;
  expandOnInit?: boolean;
  closeOnSelect?: boolean;
  closeOnOutsideClick?: boolean;
  buttonsPerRow?: number;
  optionsGap?: string | number;
  triggerBoxStyle?: NavigationButtonStyleConfig;
  optionButtonStyle?: NavigationButtonStyleConfig;
  options?: TextWidgetNavigationOption[];
}

@Component({
  selector: 'app-text-widget-navigation-n',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './text-widget-navigation-n.component.html',
  styleUrl: './text-widget-navigation-n.component.scss'
})
export class TextWidgetNavigationNComponent {
  private readonly router = inject(Router);

  private config?: TextWidgetNavigationNConfig;

  @Input() set data(value: TextWidgetNavigationNConfig | undefined) {
    this.config = value;
  }

  get data(): TextWidgetNavigationNConfig | undefined {
    return this.config;
  }

  get textCase(): TextCaseMode {
    return (this.data?.textCase || 'none') as TextCaseMode;
  }

  get navigationOptions(): TextWidgetNavigationOption[] {
    return Array.isArray(this.data?.options) ? this.data.options : [];
  }

  get hasOptions(): boolean {
    return this.navigationOptions.length > 0;
  }

  get color(): string {
    return this.data?.color || 'var(--dvm-content-text, #111827)';
  }

  get buttonsGridTemplateColumns(): string {
    const count = Number(this.data?.buttonsPerRow);
    return Number.isFinite(count) && count > 0
      ? `repeat(${count}, minmax(0, 1fr))`
      : 'repeat(auto-fit, minmax(140px, 1fr))';
  }

  get optionsGap(): string {
    return this.withDefaultUnit(this.data?.optionsGap, '8px');
  }

  onOptionClick(option: TextWidgetNavigationOption, event: Event): void {
    event.stopPropagation();
    if (option.disabled) {
      return;
    }

    const navigated = executeNavigationFromConfig(
      this.router,
      this.buildNavigationData(option),
      {
        name: this.getOptionLabel(option),
        value: option.navigateTo ?? option.route ?? option.path ?? option.url,
      }
    );

    void navigated;
  }

  formatOptionLabel(option: TextWidgetNavigationOption): string {
    return formatDisplayText(this.getOptionLabel(option), this.textCase);
  }

  getOptionModuleLabel(option: TextWidgetNavigationOption): string {
    return formatDisplayText(option.moduleLabel, this.textCase);
  }

  getOptionSubtitle(option: TextWidgetNavigationOption): string {
    return String(option.subtitle ?? '');
  }

  getOptionTopIcon(option: TextWidgetNavigationOption): string {
    return String(option.topIcon ?? '');
  }

  getOptionBottomIcon(option: TextWidgetNavigationOption): string {
    return String(option.bottomIcon ?? '');
  }

  isImageIcon(icon: string | undefined | null): boolean {
    const value = String(icon || '').trim().toLowerCase();
    return value.includes('/') || value.endsWith('.svg') || value.endsWith('.png') || value.endsWith('.jpg') || value.endsWith('.jpeg') || value.endsWith('.webp');
  }

  getOptionBackgroundColor(option: TextWidgetNavigationOption): string {
    return (
      option.buttonStyle?.backgroundColor ||
      this.data?.triggerBoxStyle?.backgroundColor ||
      this.data?.optionButtonStyle?.backgroundColor ||
      'var(--dvm-card-bg, #ffffff)'
    );
  }

  getOptionTextColor(option: TextWidgetNavigationOption): string {
    return (
      option.buttonStyle?.color ||
      this.data?.triggerBoxStyle?.color ||
      this.data?.optionButtonStyle?.color ||
      this.color
    );
  }

  getOptionBorderColor(option: TextWidgetNavigationOption): string {
    return (
      option.buttonStyle?.borderColor ||
      this.data?.triggerBoxStyle?.borderColor ||
      this.data?.optionButtonStyle?.borderColor ||
      'var(--dvm-filter-border-color, #d1d5db)'
    );
  }

  getOptionBorderWidth(option: TextWidgetNavigationOption): string {
    return this.withDefaultUnit(
      option.buttonStyle?.borderWidth ??
      this.data?.triggerBoxStyle?.borderWidth ??
      this.data?.optionButtonStyle?.borderWidth,
      '1px'
    );
  }

  getOptionBorderRadius(option: TextWidgetNavigationOption): string {
    return this.withDefaultUnit(
      option.buttonStyle?.borderRadius ??
      this.data?.triggerBoxStyle?.borderRadius ??
      this.data?.optionButtonStyle?.borderRadius,
      '8px'
    );
  }

  getOptionBoxShadow(option: TextWidgetNavigationOption): string {
    return String(
      option.buttonStyle?.boxShadow ||
      this.data?.triggerBoxStyle?.boxShadow ||
      this.data?.optionButtonStyle?.boxShadow ||
      'none'
    );
  }

  getOptionPadding(option: TextWidgetNavigationOption): string {
    return String(
      option.buttonStyle?.padding ||
      this.data?.triggerBoxStyle?.padding ||
      this.data?.optionButtonStyle?.padding ||
      '12px 14px'
    );
  }

  getOptionMinHeight(option: TextWidgetNavigationOption): string {
    return this.withDefaultUnit(
      option.buttonStyle?.minHeight ??
      this.data?.triggerBoxStyle?.minHeight ??
      this.data?.optionButtonStyle?.minHeight,
      '48px'
    );
  }

  getOptionMinWidth(option: TextWidgetNavigationOption): string {
    return this.withDefaultUnit(
      option.buttonStyle?.minWidth ??
      this.data?.triggerBoxStyle?.minWidth ??
      this.data?.optionButtonStyle?.minWidth,
      '0'
    );
  }

  getOptionWidth(option: TextWidgetNavigationOption): string | null {
    const value =
      option.buttonStyle?.width ??
      this.data?.triggerBoxStyle?.width ??
      this.data?.optionButtonStyle?.width;
    if (value === undefined || value === null || value === '') {
      return null;
    }
    return this.withDefaultUnit(value, 'auto');
  }

  getOptionFontSize(option: TextWidgetNavigationOption): string {
    return this.withDefaultUnit(
      option.buttonStyle?.font?.size ??
      this.data?.triggerBoxStyle?.font?.size ??
      this.data?.optionButtonStyle?.font?.size,
      this.withDefaultUnit(this.data?.font?.size, '14px')
    );
  }

  getOptionFontWeight(option: TextWidgetNavigationOption): string {
    return String(
      option.buttonStyle?.font?.weight ??
      this.data?.triggerBoxStyle?.font?.weight ??
      this.data?.optionButtonStyle?.font?.weight ??
      this.data?.font?.weight ??
      400
    );
  }

  getOptionFontStyle(option: TextWidgetNavigationOption): string {
    return (
      option.buttonStyle?.font?.style ||
      this.data?.triggerBoxStyle?.font?.style ||
      this.data?.optionButtonStyle?.font?.style ||
      this.data?.font?.style ||
      'normal'
    );
  }

  getOptionFontFamily(option: TextWidgetNavigationOption): string {
    return (
      option.buttonStyle?.font?.family ||
      this.data?.triggerBoxStyle?.font?.family ||
      this.data?.optionButtonStyle?.font?.family ||
      this.data?.font?.family ||
      'inherit'
    );
  }

  trackByIndex(index: number): number {
    return index;
  }

  private getOptionLabel(option: TextWidgetNavigationOption): string {
    return String(option.label ?? option.text ?? option.content ?? '');
  }

  private buildNavigationData(option: TextWidgetNavigationOption): Record<string, any> {
    const optionRoute = option.navigateTo ?? option.route ?? option.path ?? option.url;
    const parentNavigateAction = this.data?.navigateAction || {};
    const optionNavigateAction = option.navigateAction
      ? { ...parentNavigateAction, ...option.navigateAction }
      : optionRoute
        ? { ...parentNavigateAction, route: optionRoute }
        : this.data?.navigateAction;

    return {
      ...this.data,
      ...option,
      navigateTo: optionRoute ?? this.data?.navigateTo,
      navigateAction: optionNavigateAction,
    };
  }

  private withDefaultUnit(value: string | number | undefined, fallback: string): string {
    if (value === undefined || value === null || value === '') {
      return fallback;
    }
    if (typeof value === 'number') {
      return `${value}px`;
    }
    return String(value);
  }

}
